import { isNil, isUndefined } from 'lodash';
import React, { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { SupportedSubformComponentValue } from '@shared/components/blocks/types';
import { MBModalOrMobile } from '@shared/components/modalOrMobile';
import { NavHeader } from '@shared/components/nav';
import { PAGE_MODAL_WIDTH } from '@shared/styles';
import { useWorkflowTemplate } from '../../hooks';
import { isAuditComponent, isStaticTextComponent, isTextComponent } from '../../utils';
import { WorkflowComponent } from './WorkflowComponent';

interface Props {
  workflowTemplateId: string;
  initialData?: Record<string, SupportedSubformComponentValue>;
  onSubmit: (subformData: Record<string, SupportedSubformComponentValue>) => void;
  isReadOnly?: boolean;
}

export function WorkflowView({ workflowTemplateId, onSubmit, initialData, isReadOnly }: Props) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const components = useMemo(
    () => workflowTemplate?.components.filter(isSubformWorkflowComponent) ?? [],
    [workflowTemplate?.components],
  );
  const [subform, setSubform] = useState(!isNil(initialData) ? initialData : {});

  const onDataChanged = (componentId: string, data: SupportedSubformComponentValue): void => {
    setSubform({ ...subform, [componentId]: data });
  };

  const submitForm = () => {
    onSubmit(subform);
  };

  return (
    <MBModalOrMobile
      desktopModalWidth={PAGE_MODAL_WIDTH}
      desktopModalHeight={'calc(95vh - 76px)' /* height of bottom button */}
      title={workflowTemplate?.name}
      onBack={submitForm}
      webOverflow={false}
      paddingOnWeb={0}
      headerOnMobile={<NavHeader title={workflowTemplate?.name} onPressLeft={submitForm} />}
    >
      <MBView style={styles.fieldContainer}>
        <WorkflowComponents
          components={components}
          initialData={initialData}
          onDataChanged={onDataChanged}
          isReadOnly={isReadOnly}
        />
      </MBView>
    </MBModalOrMobile>
  );
}

function WorkflowComponents({
  components,
  initialData,
  onDataChanged,
  isReadOnly,
}: {
  components: MBAPI.WorkflowTemplate_components[];
  initialData?: Record<string, SupportedSubformComponentValue>;
  onDataChanged: (componentId: string, data: SupportedSubformComponentValue) => void;
  isReadOnly?: boolean;
}) {
  return (
    <>
      {components.map((component: MBAPI.WorkflowTemplate_components) => {
        return (
          <WorkflowComponent
            key={component.id}
            component={component}
            initialData={applyDefaultValue(component, initialData?.[component.id])}
            onDataChanged={onDataChanged}
            isReadOnly={isReadOnly}
          />
        );
      })}
    </>
  );
}

function isSubformWorkflowComponent(component: MBAPI.WorkflowTemplate_components): boolean {
  return isAuditComponent(component) || isStaticTextComponent(component);
}

const styles = StyleSheet.create({
  fieldContainer: {
    width: '100%',
    maxWidth: 456, // copied from Figma
    alignSelf: 'center',
  },
});

function applyDefaultValue(
  component: MBAPI.WorkflowTemplate_components,
  initialDataValue?: SupportedSubformComponentValue,
): SupportedSubformComponentValue | undefined {
  if (isTextComponent(component) && isUndefined(initialDataValue)) {
    return component.default?.text;
  }
  return initialDataValue;
}
